/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React from 'react';
import IconButton from '@mui/material/IconButton';
import isNil from 'lodash/isNil';
import { keys } from 'ramda';

import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Authorities } from '../../../../shared/entities/user';
import { FEATURE_FLAGS, requireFeatureFlag } from '../../../../utils/FeatureFlagUtils';
import { areAllowedRolesPresent, requireRBACRoles } from '../../../../utils/RBACUtils';
import { breakpoints } from '../../../materialui_wrappers/utils/StyledComponentsBreakpoints';
import VerticalTabs, { Tab } from '../../../materialui_wrappers/VerticalTabs';
import StyledProps from '../../theme/AppThemeInterface';
import MenuItems, { menuContent, MenuItem } from './MenuItems';
import { shallowEqual, useSelector } from 'react-redux';
import { selectUserData } from '../../../../store';
import { Tooltip } from '@mui/material';
import appTheme from '../../../common/theme/appTheme.json';

const StyledVerticalTabs = styled(VerticalTabs)`
  background-color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor};
  height: calc(100% - ${({ theme }: StyledProps) => theme.appHeaderHeightSmall});
  position: relative;
  top: calc(${({ theme }: StyledProps) => theme.appHeaderHeightSmall} - 0.5px);

  .appnavigation__maintenanceicon {
    width: 20px;
    height: 20px;
  }
`;

const StyledIconButton = styled(IconButton)`
  min-height: ${({ theme }: StyledProps) => theme.appHeaderHeightSmall};
  max-height: ${({ theme }: StyledProps) => theme.appHeaderHeightSmall};

  @media only screen and (min-width: ${breakpoints.sm}px) {
    min-height: ${({ theme }: StyledProps) => theme.appHeaderHeightLarge};
    max-height: ${({ theme }: StyledProps) => theme.appHeaderHeightLarge};
  }
`;

interface MenuProps extends RouteComponentProps {
  location: Location;
  drawerOpen: boolean;
}

const Menu = (props: MenuProps) => {
  const userData = useSelector(selectUserData, shallowEqual);
  const { location, drawerOpen } = props;

  return (
    <StyledVerticalTabs>
      {keys(menuContent).map((menuItem: string) => {
        const selected = location.pathname.startsWith(`/${menuItem}`);
        const menuItemConfig: MenuItem = menuContent[menuItem];

        // This menu item gets special treatment
        if (menuItem === MenuItems.plans) {
          return (
            <MissionPlansWrappedRole
              drawerOpen={drawerOpen}
              menuItem={menuItem}
              link={`/${menuItem}`}
              selected={selected}
              key={menuItem}
            />
          );
        }

        // Menu items are visible if either of the following is true:
        // * The MenuItem doesn't have any role restrictions.
        // * The MenuItem has role restrictions, and the user has any _one_ of the required roles.
        if (
          !menuItemConfig.restrictedToRoles ||
          (!isNil(userData) && areAllowedRolesPresent(userData, ...menuItemConfig.restrictedToRoles))
        ) {
          return (
            <MenuItemLink
              drawerOpen={drawerOpen}
              menuItem={menuItem}
              link={`/${menuItem}`}
              selected={selected}
              key={menuItem}
            />
          );
        } else {
          return null;
        }
      })}
    </StyledVerticalTabs>
  );
};

export default withRouter(Menu);

interface MenuItemLinkProps {
  menuItem: string;
  link: string;
  selected: boolean;
  target?: string;
  drawerOpen: boolean;
}

const MenuItemLink = (props: MenuItemLinkProps) => {
  const { menuItem, link, target, selected, drawerOpen } = props;

  const menuObj = menuContent[menuItem];
  const targetLink = !isNil(target) ? target : '';
  const linkUrl = !isNil(link) ? link : '';
  const icon = <StyledIconButton aria-label="Menu Item">{menuObj.icon}</StyledIconButton>;

  return menuObj.visible ? (
    <Tooltip
      title={drawerOpen ? '' : menuContent[menuItem].text}
      placement="right-start"
      componentsProps={{
        tooltip: {
          sx: { backgroundColor: appTheme.tooltipBackgroundColor },
        },
      }}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [10, -10],
            },
          },
        ],
      }}
    >
      <Link to={linkUrl} key={menuItem} target={targetLink}>
        <Tab icon={icon} key={menuItem} id={menuItem} text={menuContent[menuItem].text} selected={selected} />
      </Link>
    </Tooltip>
  ) : null;
};

const MissionPlansWrappedRole = requireFeatureFlag(
  requireRBACRoles(MenuItemLink, Authorities.ROLE_MPT_MENU_VIEW),
  FEATURE_FLAGS.isMptEnabled,
);
